// All of the app's CSS, injected once. Kept in one string rather than a .css file so
// the shell's markup and its styling are read side by side — the layout is small
// enough that splitting them costs more than it saves.

export const CSS = `
:root {
  --bg: #070b14;
  --bg-lift: rgba(18, 26, 44, 0.66);
  --edge: rgba(255, 255, 255, 0.09);
  --ink: #e8f6ff;
  --dim: #93a4bd;
  --cyan: #5ee7ff;
  --mag: #b98cff;
  --good: #43ffa8;
  --warn: #ffc75a;
  --bad: #ff7b7b;
  --r: 14px;
  --dock: 84px;
}
* { margin: 0; padding: 0; box-sizing: border-box; }
html, body { height: 100%; }
body {
  background:
    radial-gradient(90% 70% at 50% -10%, #17224a 0%, transparent 60%),
    radial-gradient(70% 60% at 100% 100%, #2a1b45 0%, transparent 55%),
    var(--bg);
  color: var(--ink);
  font: 15px/1.5 system-ui, -apple-system, "Segoe UI", sans-serif;
  -webkit-font-smoothing: antialiased;
  overflow: hidden;
}
button, select, input, textarea { font: inherit; color: inherit; }
button { cursor: pointer; }
:focus-visible { outline: 2px solid var(--cyan); outline-offset: 2px; }

.glass {
  background: var(--bg-lift);
  border: 1px solid var(--edge);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  box-shadow: 0 10px 34px rgba(0, 0, 0, 0.36), inset 0 1px 0 rgba(255, 255, 255, 0.05);
}

#vb {
  position: fixed;
  inset: 0;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
  gap: 12px;
  /* max() over the safe areas, not a plain sum: a notch or a home indicator must not
     eat the dock, and on everything else these resolve to 0 and the padding stands. */
  padding: max(12px, env(safe-area-inset-top)) max(clamp(12px, 2.4vw, 22px), env(safe-area-inset-right))
    max(14px, env(safe-area-inset-bottom)) max(clamp(12px, 2.4vw, 22px), env(safe-area-inset-left));
}

/* ── header ───────────────────────────────────────────────────────────────── */
header {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 40px;
}
.wordmark {
  font-weight: 800;
  letter-spacing: 0.22em;
  font-size: 15px;
  text-shadow: 0 0 18px rgba(94, 231, 255, 0.5);
  white-space: nowrap;
}
.tagline { color: var(--dim); font-size: 12px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.grow { flex: 1 1 auto; min-width: 0; }
.chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 11px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid var(--edge);
}
.chip b { font-weight: 800; color: var(--cyan); }
button.chip { cursor: pointer; }
button.chip:hover { background: rgba(255, 255, 255, 0.12); }
.chip.placement-client b { color: var(--mag); }
.icon-btn {
  width: 38px; height: 38px;
  display: grid; place-items: center;
  border-radius: 999px;
  border: 1px solid var(--edge);
  background: rgba(255, 255, 255, 0.05);
  font-size: 16px;
  transition: background 0.15s, transform 0.1s;
}
.icon-btn:hover { background: rgba(255, 255, 255, 0.12); }
.icon-btn:active { transform: scale(0.94); }

/* ── stage ────────────────────────────────────────────────────────────────── */
main {
  display: grid;
  grid-template-columns: minmax(0, 1fr) clamp(280px, 30%, 380px);
  gap: 12px;
  min-height: 0;
}
#stage {
  position: relative;
  /* One grid cell holds the face. A mounted face may set inline styles on its own
     container (the vrm face sets position:relative so it can place its caption),
     which would beat a stylesheet position:absolute and collapse the box to the
     canvas — and the canvas is sized FROM the box. As a stretched grid item the
     height comes from the stage either way. */
  display: grid;
  border-radius: 20px;
  overflow: hidden;
  min-height: 0;
}
#face { grid-area: 1 / 1; position: relative; min-width: 0; min-height: 0; overflow: hidden; }
/* A .vrm dragged over the face: the only way an avatar ever gets in. */
#stage[data-drop="true"]::after {
  content: "drop your .vrm or .mavatar to wear it";
  position: absolute;
  inset: 10px;
  border: 2px dashed rgba(94, 231, 255, 0.6);
  border-radius: 16px;
  display: grid;
  place-items: center;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: var(--cyan);
  background: rgba(7, 11, 20, 0.55);
}
#face canvas { display: block; width: 100%; height: 100%; }
#pill {
  /* Top-left, not bottom-centre: a face may place its own caption along the bottom
     (the vrm face does), and status must never sit on top of the face's own words. */
  position: absolute;
  top: 12px;
  left: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 14px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  white-space: nowrap;
  max-width: calc(100% - 24px);
}
#pill .dot {
  width: 9px; height: 9px;
  border-radius: 50%;
  background: var(--dim);
  box-shadow: 0 0 10px currentColor;
  flex: none;
}
#pill[data-conn="live"] .dot { background: var(--good); animation: breathe 2.4s ease-in-out infinite; }
#pill[data-conn="opening"] .dot { background: var(--warn); animation: breathe 0.9s ease-in-out infinite; }
#pill[data-conn="error"] .dot { background: var(--bad); }
#pill .what { color: var(--dim); font-weight: 600; }
@keyframes breathe { 0%, 100% { opacity: 1; } 50% { opacity: 0.35; } }

/* ── first-run guide (the empty transcript) ───────────────────────────────── */
.coach { display: grid; gap: 12px; }
.coach-title { font-size: 10px; font-weight: 800; letter-spacing: 0.18em; text-transform: uppercase; color: var(--dim); }
.coach ol { display: grid; gap: 11px; margin: 0; padding: 0; list-style: none; counter-reset: step; }
.coach li { display: grid; gap: 2px; padding-left: 28px; position: relative; font-size: 13px; counter-increment: step; }
.coach li::before {
  content: counter(step);
  position: absolute;
  left: 0;
  top: 0;
  width: 20px; height: 20px;
  display: grid; place-items: center;
  border-radius: 50%;
  background: rgba(94, 231, 255, 0.16);
  border: 1px solid rgba(94, 231, 255, 0.3);
  font-size: 11px;
  font-weight: 800;
  color: var(--cyan);
}
.coach li b { font-weight: 700; }
.coach li span { color: var(--dim); font-size: 12px; }
.coach code { font-family: ui-monospace, Menlo, monospace; font-size: 11px; padding: 1px 5px; border-radius: 5px; background: rgba(255,255,255,.08); }
.coach .mic-state[data-state="granted"] { color: var(--good); }
.coach .mic-state[data-state="denied"] { color: var(--bad); }
.coach > .ghost { justify-self: start; }

/* ── provider picker ──────────────────────────────────────────────────────── */
.providers { display: grid; gap: 6px; }
.provider {
  display: grid;
  gap: 2px;
  text-align: left;
  padding: 9px 11px;
  border-radius: 11px;
  border: 1px solid var(--edge);
  background: rgba(8, 12, 22, 0.6);
  transition: background 0.14s, border-color 0.14s;
}
.provider:hover { background: rgba(255, 255, 255, 0.07); }
.provider[aria-pressed="true"] { border-color: rgba(94, 231, 255, 0.5); background: rgba(94, 231, 255, 0.12); }
.provider[data-usable="false"] { opacity: 0.5; }
.provider b { font-size: 13px; font-weight: 700; }
.provider .row { justify-content: space-between; gap: 8px; }
.provider .tag {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--dim);
  white-space: nowrap;
}
.provider[data-usable="false"] .tag { color: var(--warn); }
.provider .why { font-size: 11px; color: var(--dim); overflow-wrap: anywhere; }

/* ── transcript ───────────────────────────────────────────────────────────── */
#log {
  border-radius: 18px;
  padding: 14px 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  scrollbar-color: rgba(255,255,255,.18) transparent;
  /* A turn scrolled off the top fades out instead of being sliced mid-line. */
  -webkit-mask-image: linear-gradient(#0000 0, #000 28px);
  mask-image: linear-gradient(#0000 0, #000 28px);
}
#log .empty { color: var(--dim); font-size: 13px; }

.turn {
  display: grid;
  gap: 5px;
  padding: 9px 12px 10px;
  border-radius: 12px;
  border-left: 3px solid var(--edge);
  background: rgba(255, 255, 255, 0.035);
  animation: rise 0.2s ease-out;
}
.turn.user { border-left-color: var(--cyan); }
.turn.assistant { border-left-color: var(--mag); background: rgba(185, 140, 255, 0.07); }
.turn.system, .turn.tool { background: transparent; padding: 2px 0 2px 12px; }

.turn .who { display: flex; align-items: center; gap: 8px; }
.turn .badge {
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--dim);
}
.turn.user .badge { color: var(--cyan); }
.turn.assistant .badge { color: var(--mag); }
.turn.tool .badge { color: var(--warn); }

.turn .what { white-space: pre-wrap; overflow-wrap: anywhere; line-height: 1.5; }
.turn.system .what { color: var(--dim); font-size: 13px; }
.turn.tool .what { font-family: ui-monospace, "SF Mono", Menlo, monospace; font-size: 12px; color: var(--warn); }

/* Live text reads as provisional: dimmed, italic, with a caret that keeps blinking
   until the final replaces it. */
.turn.partial .what { color: var(--dim); font-style: italic; }
.turn.partial .what::after {
  content: "▌";
  margin-left: 2px;
  animation: blink 1.05s step-end infinite;
}
.turn.partial .badge::after { content: " · hearing"; letter-spacing: 0.04em; opacity: 0.7; }
@keyframes blink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }

/* One long answer must not bury the conversation; the text stays, folded. */
.turn .what.folded {
  max-height: 11.5lh;
  overflow: hidden;
  -webkit-mask-image: linear-gradient(#000 70%, #0000 100%);
  mask-image: linear-gradient(#000 70%, #0000 100%);
}
.unfold {
  justify-self: start;
  border: none;
  background: transparent;
  padding: 2px 0;
  font-size: 12px;
  font-weight: 700;
  color: var(--cyan);
}
.unfold:hover { text-decoration: underline; }
@keyframes rise { from { opacity: 0; transform: translateY(5px); } to { opacity: 1; transform: none; } }

/* ── dock ─────────────────────────────────────────────────────────────────── */
footer {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 18px;
  flex-wrap: wrap;
}
#talk {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 22px;
  border-radius: 999px;
  border: 1px solid rgba(94, 231, 255, 0.4);
  background: linear-gradient(135deg, rgba(94, 231, 255, 0.24), rgba(185, 140, 255, 0.24));
  font-weight: 700;
  letter-spacing: 0.04em;
  transition: transform 0.1s, filter 0.15s;
  white-space: nowrap;
}
/* Drawn, not typed: an emoji glyph is one missing font away from a tofu box. */
#talk .led {
  width: 10px; height: 10px;
  border-radius: 50%;
  background: var(--cyan);
  box-shadow: 0 0 10px var(--cyan);
  flex: none;
}
#talk[data-live="true"] .led { border-radius: 2px; background: var(--bad); box-shadow: 0 0 10px var(--bad); }
#talk:hover { filter: brightness(1.15); }
#talk:active { transform: scale(0.97); }
#talk[data-live="true"] { border-color: rgba(255, 123, 123, 0.45); background: rgba(255, 123, 123, 0.16); }
#talk[disabled] { opacity: 0.55; cursor: progress; }
.level { display: flex; align-items: center; gap: 10px; flex: 1 1 200px; min-width: 120px; }
#meter {
  flex: 1 1 90px;
  min-width: 60px;
  height: 8px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  overflow: hidden;
}
/* No session, no microphone, nothing to measure — an empty bar is just a line the
   eye has to explain to itself. The hint beside it carries the state instead. */
#vb:not([data-conn="live"]) #meter { display: none; }
#meter > i {
  display: block;
  height: 100%;
  width: 0;
  border-radius: 999px;
  background: linear-gradient(90deg, var(--cyan), var(--mag));
  transition: width 0.08s linear;
}
#say {
  flex: 2 1 180px;
  min-width: 120px;
  padding: 10px 14px;
  border-radius: 999px;
  border: 1px solid var(--edge);
  background: rgba(255, 255, 255, 0.05);
}
#say::placeholder { color: var(--dim); }
.hint { color: var(--dim); font-size: 12px; white-space: nowrap; }
.seg { display: flex; gap: 2px; padding: 3px; border-radius: 999px; background: rgba(255, 255, 255, 0.06); border: 1px solid var(--edge); }
.seg button {
  border: none;
  background: transparent;
  padding: 7px 13px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  color: var(--dim);
  white-space: nowrap;
}
.seg button[aria-pressed="true"] { background: rgba(94, 231, 255, 0.2); color: var(--ink); }

/* ── errors ───────────────────────────────────────────────────────────────── */
#alerts {
  position: fixed;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  width: min(620px, calc(100vw - 24px));
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 40;
}
.alert {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 11px 13px;
  border-radius: 12px;
  border-left: 3px solid var(--bad);
  font-size: 13px;
  animation: rise 0.2s ease-out;
}
.alert.notice { border-left-color: var(--warn); }
.alert .body { flex: 1; min-width: 0; }
.alert .head { font-weight: 700; overflow-wrap: anywhere; }
.alert .detail { color: var(--dim); font-size: 12px; margin-top: 3px; overflow-wrap: anywhere; }
.alert .x { border: none; background: transparent; color: var(--dim); font-size: 15px; line-height: 1; padding: 2px 4px; }

#scrim {
  position: fixed;
  inset: 0;
  z-index: 45;
  background: rgba(4, 7, 14, 0.55);
  backdrop-filter: blur(1.5px);
  -webkit-backdrop-filter: blur(1.5px);
  animation: fade 0.18s ease-out;
}
@keyframes fade { from { opacity: 0; } to { opacity: 1; } }

/* ── settings ─────────────────────────────────────────────────────────────── */
#settings {
  /* Nearly opaque: the header sitting behind a translucent drawer reads as a smudge. */
  background: rgba(10, 14, 26, 0.96);
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: min(400px, 100vw);
  padding: 18px 18px 32px;
  overflow-y: auto;
  z-index: 50;
  border-radius: 0;
  border-right: none;
  transform: translateX(101%);
  transition: transform 0.24s cubic-bezier(0.3, 0.9, 0.3, 1);
  display: flex;
  flex-direction: column;
  gap: 14px;
}
#settings[data-open="true"] { transform: none; }
#settings h2 { font-size: 13px; letter-spacing: 0.16em; text-transform: uppercase; color: var(--dim); font-weight: 800; }
.field { display: grid; gap: 5px; }
.field > label { font-size: 12px; font-weight: 700; color: var(--dim); }
.field .note { font-size: 11px; color: var(--dim); }
.field select, .field input, .field textarea {
  padding: 9px 11px;
  border-radius: 10px;
  border: 1px solid var(--edge);
  background: rgba(8, 12, 22, 0.72);
  width: 100%;
}
.field textarea { resize: vertical; min-height: 84px; line-height: 1.45; }
#settings input[type="file"] { padding: 7px 9px; font-size: 13px; }
#settings input[type="file"]::file-selector-button {
  margin-right: 10px;
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid var(--edge);
  background: rgba(255, 255, 255, 0.07);
  color: var(--ink);
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}
#settings input[type="file"]::file-selector-button:hover { background: rgba(255, 255, 255, 0.14); }
.row { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
.ghost {
  padding: 9px 14px;
  border-radius: 10px;
  border: 1px solid var(--edge);
  background: rgba(255, 255, 255, 0.05);
  font-size: 13px;
  font-weight: 600;
}
.ghost:hover { background: rgba(255, 255, 255, 0.11); }
#apply { border-color: rgba(94, 231, 255, 0.45); background: rgba(94, 231, 255, 0.18); }
.sep { height: 1px; background: var(--edge); }

/* ── the phone ─────────────────────────────────────────────────────────────────
   Not a reflow of the desktop layout. A phone gets the header (so settings are
   reachable at all), the face, the transcript and one obvious talk control under the
   thumb. Four blocks, in this order, and each answers one question:
     narrow          — chrome and dock that suit a thumb, in either orientation
     phone-width     — how the transcript and the first-run guide read
     narrow and tall — portrait: one column
     short           — landscape, or portrait with the on-screen keyboard up */
@media (max-width: 860px) {
  .tagline { display: none; }
  .wordmark { font-size: 13px; letter-spacing: 0.18em; }
  header { gap: 8px; min-height: 44px; }
  /* Sized for a thumb, not a mouse: the gear is the only route to the provider
     picker, the persona and the placement, so it must be genuinely hittable. */
  .icon-btn { width: 44px; height: 44px; font-size: 17px; }
  .chip { padding: 7px 12px; }

  /* An alert takes a row of the layout rather than floating over the top of it. As a
     fixed overlay it covered the whole header — brand, session chip and gear — which
     is what made the app look on a phone as though it had no header and no settings,
     and it sat on the status pill besides. Above the dock it is also where the thumb
     already is to dismiss it. */
  #alerts {
    position: static;
    transform: none;
    width: auto;
    z-index: auto;
  }
  #alerts:empty { display: none; }
  .alert .x { padding: 6px 9px; font-size: 17px; }

  /* Full width already; it also has to clear the notch and the home indicator, or
     the provider picker starts under one and Apply ends under the other. */
  #settings { padding: max(18px, env(safe-area-inset-top)) 18px max(32px, env(safe-area-inset-bottom)); }

  /* The dock, in reading order: the one thing to press, then how turns are taken and
     what the mic is hearing, then the typed fallback. */
  footer { gap: 9px; padding: 10px; }
  #talk { flex: 1 1 100%; justify-content: center; padding: 15px 22px; font-size: 16px; }
  #push { flex: 1 1 100%; justify-content: center; }
  #stop { flex: 0 0 auto; }
  .seg { flex: 0 0 auto; }
  /* A live hint ("Just talk — it is listening") shared a row with the toggle and got
     clipped mid-word, so while there is a session it takes its own. Idle it is two
     words and the meter is hidden, so it sits beside the toggle and the face wins
     back the height. */
  .level { flex: 1 1 100%; }
  #vb:not([data-conn="live"]) .level { flex: 1 1 90px; min-width: 80px; }
  #say { order: 5; flex: 1 1 100%; }
}

/* ── phone width: one column, the face on top ─────────────────────────────────
   Three glass cards of equal weight is no hierarchy at all, so the transcript drops
   its card and reads as content between the face and the dock. */
@media (max-width: 620px) {
  #log {
    background: none;
    border: none;
    box-shadow: none;
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
    border-radius: 0;
    padding: 6px 2px 0;
  }
  /* Same three steps, a third of the height: each one is a line, not a paragraph.
     It used to take as much of the screen as the face it was explaining. */
  .coach { gap: 9px; }
  .coach ol { gap: 8px; }
  .coach li { display: block; padding-left: 26px; font-size: 13px; }
  .coach li b { display: inline; }
  .coach li span { display: inline; margin-left: 7px; }
}

/* Narrow and tall — portrait. One column, and the face leads it. */
@media (max-width: 860px) and (min-height: 560px) {
  main { grid-template-columns: minmax(0, 1fr); grid-template-rows: minmax(0, 1.45fr) minmax(120px, 1fr); }
}

/* ── short: a landscape phone, or a portrait one with the keyboard up ──────────
   Chrome and the dock give up their padding before the face does. */
@media (max-height: 560px) {
  #vb { gap: 8px; padding-top: max(8px, env(safe-area-inset-top)); padding-bottom: max(8px, env(safe-area-inset-bottom)); }
  header { min-height: 38px; }
  footer { gap: 8px; padding: 8px 10px; }
  #talk { padding: 12px 20px; font-size: 15px; }
  .coach { gap: 7px; }
  .coach ol { gap: 6px; }
  .coach li { display: block; font-size: 12px; padding-left: 24px; }
  .coach li span:not(.mic-state) { display: none; }
  .coach li .mic-state { display: inline; margin-left: 7px; }
}

/* Short but wide enough for two columns — a landscape phone. Stacking here is what
   crushed the transcript to nothing behind the dock, so the columns stay. */
@media (max-height: 560px) and (min-width: 620px) {
  main { grid-template-columns: minmax(0, 1fr) clamp(240px, 38%, 340px); grid-template-rows: minmax(0, 1fr); }
  #log { padding: 10px 12px 12px; gap: 10px; }
  footer { flex-wrap: nowrap; }
  #talk { flex: 0 0 auto; }
  #push { flex: 0 0 auto; }
  .level { flex: 0 1 110px; min-width: 0; }
  /* The status pill already says it, and a landscape dock has one row to spend. */
  .hint { display: none; }
  #say { order: 0; flex: 1 1 130px; }
}

/* Short AND narrow — a phone in portrait with the on-screen keyboard open.
   There is no height left to be a face in, and half a face is worse than none: the
   orb's camera has a fixed vertical field of view, so a short stage clips the sphere
   instead of shrinking it, and the status pill ends up sitting on top of what is
   left. The stage therefore stands down to the status bar it was already becoming,
   and the transcript takes the room — which is what you want on screen while typing
   a turn anyway. Nothing is unmounted, only hidden: face-host's fit() skips a 0x0
   box, so the face comes straight back with the keyboard. */
@media (max-height: 560px) and (max-width: 620px) {
  main { grid-template-columns: minmax(0, 1fr); grid-template-rows: auto minmax(0, 1fr); }
  #stage {
    background: none;
    border: none;
    box-shadow: none;
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
    border-radius: 0;
    overflow: visible;
  }
  #face { display: none; }
  #pill { position: static; justify-self: start; }
}
`
